import React, { ReactNode, useEffect } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton as MuiIconButton,
} from '@mui/material';
import styles from './ContentDialog.module.scss';
import CloseIcon from '@mui/icons-material/Close';
import { colors } from '../../../styles/colors';
import { translate } from '../../../utils/translation/translation';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { translationsState } from '../../../atoms/translation/translation.atom';
import { modalOpenState } from '../../../atoms/layout/layout.atom';
import { Breakpoint } from '@mui/system';
import { ApiUrls } from '../../List/ListForm/FormContent/FormContent';

export type AdditionalSubmitAction = {
  onSubmit?: (e: any) => void;
  label?: string;
  apiUrls?: ApiUrls;
};

export type ContentDialogProps = {
  isOpen: boolean;
  onClose: (e: any) => void;
  onSubmit: (e: any) => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  error?: string;
  isDisabled?: boolean;
  fullWidth?: boolean;
  displayActionButtons?: boolean;
  maxWidth?: Breakpoint;
  isSecure?: boolean;
  doNotCloseOnBackdropClick?: boolean;
  additionalSubmitActions?: AdditionalSubmitAction[];
  submitButtonTitle?: string;
  isLoading?: boolean;
};

export const ContentDialog = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  subtitle,
  children,
  error,
  isDisabled,
  fullWidth = true,
  displayActionButtons = true,
  maxWidth = 'md',
  isSecure,
  doNotCloseOnBackdropClick,
  additionalSubmitActions,
  submitButtonTitle,
  isLoading,
}: ContentDialogProps) => {
  const translations = useRecoilValue(translationsState);
  const setIsModalOpen = useSetRecoilState(modalOpenState);

  useEffect(() => {
    setIsModalOpen(isOpen);
  }, [isOpen]);

  const handleClose = (e: any, reason?: string) => {
    if (reason === 'backdropClick' && isSecure) {
      return;
    }

    if (reason === 'backdropClick' && doNotCloseOnBackdropClick) {
      return;
    }

    setIsModalOpen(false);
    onClose(e);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      disableEscapeKeyDown={isSecure || doNotCloseOnBackdropClick}
      slotProps={
        isSecure
          ? {
              backdrop: {
                sx: {
                  backdropFilter: 'blur(6px)',
                },
              },
            }
          : undefined
      }
      onClick={(e) => e.stopPropagation()}
    >
      {title && (
        <DialogTitle className={styles.dialogTitle}>
          {title}
          <MuiIconButton onClick={handleClose} size="small">
            <CloseIcon fontSize={'small'} />
          </MuiIconButton>
        </DialogTitle>
      )}
      <DialogContent
        className={styles.dialogContent}
        style={{ color: colors.textSubtitleColor }}
      >
        {subtitle}
        {children}
      </DialogContent>
      {displayActionButtons && (
        <DialogActions className={styles.dialogActions}>
          {error && (
            <Alert variant={'standard'} severity={'error'} color={'error'}>
              {error}
            </Alert>
          )}
          <Button onClick={handleClose} variant={'outlined'}>
            {translate(translations, 'MODAL_HEADER_ACTION.CANCEL')}
          </Button>
          {additionalSubmitActions?.map((additionalSubmitAction, index) => (
            <Button
              key={`additional-button-${index}`}
              variant={'outlined'}
              onClick={(e) => additionalSubmitAction.onSubmit?.(e)}
              disabled={isDisabled}
            >
              {additionalSubmitAction.label ?? ''}
            </Button>
          ))}
          <Button
            variant={'contained'}
            onClick={onSubmit}
            disabled={isDisabled}
            startIcon={
              isLoading ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {submitButtonTitle ??
              translate(translations, 'MODAL_HEADER_ACTION.SUBMIT')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ContentDialog;
